'use client';

import type { Transition } from 'framer-motion';
import type { FC, PropsWithChildren } from 'react';
import cn from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { RemoveScroll } from 'react-remove-scroll';
import {
  TRANSITION_DURATION_BASE,
  TRANSITION_DURATION_SHORT,
} from '@/core/constants/tokens';
import useTranslations from '@/core/hooks/use-translations';
import Button from '@/ui/components/Button/Button';
import VectorGraphic from '@/ui/components/VectorGraphic/VectorGraphic';
import useBreakpoints from '@/ui/hooks/use-breakpoints';

import $ from './FlyInPanel.module.scss';

export type FlyInPanelProps = PropsWithChildren<{
  isOpen: boolean;
  onClose: () => void;
  onSubmit?: () => void;
  closeButtonText?: string;
  submitButtonText?: string;
  className?: string;
  ['aria-label']?: string;
  ['data-t']?: string;
  ['data-testid']?: string;
  withHeader?: boolean;
  submitDisabled?: boolean;
  classNameBottomBar?: string;
}>;

const FlyInPanel: FC<FlyInPanelProps> = ({
  isOpen,
  onClose,
  onSubmit,
  closeButtonText,
  submitButtonText,
  children,
  className,
  'aria-label': ariaLabel,
  'data-t': dataT,
  'data-testid': dataTestId,
  withHeader = true,
  submitDisabled = false,
  classNameBottomBar,
}) => {
  const t = useTranslations();
  const { isDesktop, isDesktopLarge } = useBreakpoints();
  const panelRef = useRef<HTMLDivElement>(null);
  const isOpenRef = useRef(isOpen);
  const [isPresent, setIsPresent] = useState(isOpen);
  const [portalNode, setPortalNode] = useState<HTMLDivElement | null>(null);

  isOpenRef.current = isOpen;

  useEffect(() => {
    if (isOpen) setIsPresent(true);
  }, [isOpen]);

  useLayoutEffect(() => {
    if (!isPresent) return () => {};

    const node = document.createElement('div');
    document.body.appendChild(node);
    setPortalNode(node);

    return () => {
      // React removes the panel from this node during the same commit.
      // Drop the node only after that deletion, or removeChild throws.
      queueMicrotask(() => {
        node.remove();
      });
    };
  }, [isPresent]);

  const durationBase = TRANSITION_DURATION_BASE / 1000;
  const durationShort = TRANSITION_DURATION_SHORT / 1000;

  const isDesktopOrLarger = isDesktop || isDesktopLarge;

  // Swipe to dismiss threshold (in pixels)
  const SWIPE_DISMISS_THRESHOLD = 100;

  const panelInitial = useMemo(() => {
    if (isDesktopOrLarger) return { x: '100%', opacity: 0 } as const;
    return { y: '100%', opacity: 0 } as const;
  }, [isDesktopOrLarger]);

  const panelAnimate = useMemo(() => ({ x: 0, y: 0, opacity: 1 }), []);

  const panelExit = useMemo(() => {
    if (isDesktopOrLarger) {
      return {
        x: '100%',
        opacity: 0,
        transition: {
          x: { duration: durationShort, ease: 'easeInOut' },
          opacity: { duration: durationBase, ease: 'easeInOut' },
        },
      } as const;
    }
    return {
      y: '100%',
      opacity: 0,
      transition: {
        y: { duration: durationShort, ease: 'easeInOut' },
        opacity: { duration: durationBase, ease: 'easeInOut' },
      },
    } as const;
  }, [isDesktopOrLarger, durationBase, durationShort]);

  const panelTransition = useMemo<Transition>(
    () => ({
      x: { duration: durationBase, ease: 'easeInOut' },
      y: { duration: durationBase, ease: 'easeInOut' },
      opacity: { duration: durationShort, ease: 'easeInOut' },
    }),
    [durationBase, durationShort],
  );

  const handleBackdropClick = () => onClose();
  const handleStopPropagation: React.MouseEventHandler<HTMLDivElement> = ev => {
    ev.stopPropagation();
  };

  // Handle swipe to dismiss on mobile
  const handleDragEnd = (
    _event: MouseEvent | TouchEvent | PointerEvent,
    info: { offset: { y: number } },
  ) => {
    if (!isDesktopOrLarger && info.offset.y > SWIPE_DISMISS_THRESHOLD) {
      onClose();
    }
  };

  const handleExitComplete = () => {
    if (!isOpenRef.current) {
      setIsPresent(false);
      setPortalNode(null);
    }
  };

  const overlay = (
    <RemoveScroll enabled={isPresent}>
      <AnimatePresence onExitComplete={handleExitComplete}>
        {isOpen && (
          <div className={$.wrapper}>
            <motion.div
              className={$.backdrop}
              onClick={handleBackdropClick}
              role="presentation"
              tabIndex={-1}
              data-t={dataT}
              data-testid={dataTestId}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{
                opacity: 0,
                transition: { duration: durationBase, ease: 'easeInOut' },
              }}
              transition={{ duration: durationBase, ease: 'easeInOut' }}
              aria-label={ariaLabel}
            >
              <motion.div
                className={[$.panel, className].filter(Boolean).join(' ')}
                ref={panelRef}
                initial={panelInitial}
                animate={panelAnimate}
                exit={panelExit}
                transition={panelTransition}
                onClick={handleStopPropagation}
                drag={!isDesktopOrLarger ? 'y' : false}
                dragConstraints={{ top: 0, bottom: 0 }}
                dragElastic={{ top: 0, bottom: 0.5 }}
                onDragEnd={handleDragEnd}
              >
                {withHeader ? (
                  <div className={$.header}>
                    <div className={$.logoWrapper}>
                      <VectorGraphic name="image-logo" className={$.logo} />
                    </div>

                    <Button
                      variant="tertiary"
                      className={$.closeButtonTopRight}
                      iconClassName={$.closeButtonIcon}
                      icon="icon-close"
                      ariaLabel={t('web_dialog_button_close')}
                      onClick={onClose}
                    />
                  </div>
                ) : (
                  <Button
                    variant="tertiary"
                    className={$.closeButtonTopRight}
                    iconClassName={$.closeButtonIcon}
                    icon="icon-close"
                    ariaLabel={t('web_dialog_button_close')}
                    onClick={onClose}
                  />
                )}

                <div className={$.content}>{children}</div>

                <div className={cn($.bottomBar, classNameBottomBar)}>
                  <Button variant="secondary" fullWidth onClick={onClose}>
                    {closeButtonText || t('web_general_button_close')}
                  </Button>
                  {onSubmit && (
                    <Button
                      variant="primary"
                      fullWidth
                      onClick={onSubmit}
                      disabled={submitDisabled}
                    >
                      {submitButtonText || t('web_general_button_submit')}
                    </Button>
                  )}
                </div>
              </motion.div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </RemoveScroll>
  );

  if (!isPresent || !portalNode) return null;
  return createPortal(overlay, portalNode);
};

export default FlyInPanel;
